import React, { useState, useEffect } from 'react';
import { Calendar, Clock, X, Trash2, Save, Users, Settings, Send, Lock, LogOut, User } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { getTelegramSettings, updateTelegramSettings, testTelegramConnection } from '../lib/telegram';
import { doctors, timeSlots } from '../data/mock';
import AdminLogin from '../components/AdminLogin';
import '../styles/admin.css';

const AdminPanel = () => {
  const [selectedDoctor, setSelectedDoctor] = useState(doctors[0]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [blockedSlots, setBlockedSlots] = useState([]);
  const [selectedTimes, setSelectedTimes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [showBookings, setShowBookings] = useState(false);
  const [showTelegramSettings, setShowTelegramSettings] = useState(false);
  const [telegramSettings, setTelegramSettings] = useState({
    bot_token: '',
    chat_username: ''
  });
  const [telegramLoading, setTelegramLoading] = useState(false);
  const [telegramMessage, setTelegramMessage] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [isClosing, setIsClosing] = useState(false);
  const [animateSlots, setAnimateSlots] = useState(0);
  const [slotsLoaded, setSlotsLoaded] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [passwordChange, setPasswordChange] = useState({
    currentPassword: '',
    newUsername: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordMessage, setPasswordMessage] = useState(null);
  const [currentUsername, setCurrentUsername] = useState('admin');

  useEffect(() => {
    loadBlockedSlots();
    if (showBookings) {
      loadBookings();
    }
    setAnimateSlots(prev => prev + 1);
  }, [selectedDoctor, selectedDate, showBookings]);

  useEffect(() => {
    if (showTelegramSettings) {
      loadTelegramSettings();
    }
  }, [showTelegramSettings]);

  const loadTelegramSettings = async () => {
    try {
      const settings = await getTelegramSettings();
      if (settings) {
        setTelegramSettings({
          bot_token: settings.bot_token,
          chat_username: settings.chat_username
        });
      }
    } catch (error) {
      console.error('Error loading Telegram settings:', error);
    }
  };

  const handleTelegramSettingsChange = (field, value) => {
    setTelegramSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveTelegramSettings = async () => {
    setTelegramLoading(true);
    setTelegramMessage(null);

    try {
      const result = await updateTelegramSettings(
        telegramSettings.bot_token,
        telegramSettings.chat_username
      );

      if (result.success) {
        setTelegramMessage({ type: 'success', text: 'Налаштування збережено!' });
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('Error saving Telegram settings:', error);
      setTelegramMessage({ type: 'error', text: 'Помилка збереження' });
    } finally {
      setTelegramLoading(false);
      setTimeout(() => setTelegramMessage(null), 3000);
    }
  };

  const handleTestTelegram = async () => {
    setTelegramLoading(true);
    setTelegramMessage(null);

    try {
      const result = await testTelegramConnection(
        telegramSettings.bot_token,
        telegramSettings.chat_username
      );

      if (result.success) {
        setTelegramMessage({
          type: 'success',
          text: `✅ Підключення успішне! Бот: ${result.botName} (@${result.botUsername})`
        });
      } else {
        setTelegramMessage({
          type: 'error',
          text: `❌ ${result.error}`
        });
      }
    } catch (error) {
      console.error('Error testing Telegram:', error);
      setTelegramMessage({ type: 'error', text: 'Помилка тестування' });
    } finally {
      setTelegramLoading(false);
    }
  };

  const loadBlockings = async () => {
    await loadBlockedSlots();
  };

  const loadBlockedSlots = async () => {
    setSlotsLoaded(false);
    try {
      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
      const day = String(selectedDate.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;
      
      // Завантажити заблоковані слоти
      const { data: blockedData, error: blockedError } = await supabase
        .from('blocked_slots')
        .select('*')
        .eq('doctor_name', selectedDoctor)
        .eq('blocked_date', dateStr);

      if (blockedError) throw blockedError;
      
      // Завантажити записи (зайняті часи)
      const { data: bookingsData, error: bookingsError } = await supabase
        .from('bookings')
        .select('*')
        .eq('doctor_name', selectedDoctor)
        .eq('booking_date', dateStr)
        .neq('status', 'cancelled');

      if (bookingsError) throw bookingsError;
      
      // Об'єднати заблоковані та зайняті часи
      const blockedTimes = blockedData?.map(slot => slot.blocked_time) || [];
      const bookedTimes = bookingsData?.map(booking => booking.booking_time) || [];
      const allBlockedTimes = [...new Set([...blockedTimes, ...bookedTimes])];
      
      setBlockedSlots(blockedData || []);
      setBookings(bookingsData || []);
      setSelectedTimes(allBlockedTimes);
      
      // Невелика затримка перед показом анімації
      setTimeout(() => {
        setSlotsLoaded(true);
      }, 50);
    } catch (error) {
      console.error('Error loading blocked slots:', error);
      setSlotsLoaded(true);
    }
  };

  const loadBookings = async () => {
    try {
      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
      const day = String(selectedDate.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;
      const { data, error } = await supabase
        .from('bookings')
        .select('*')
        .eq('doctor_name', selectedDoctor)
        .eq('booking_date', dateStr)
        .order('booking_time', { ascending: true });

      if (error) throw error;
      setBookings(data || []);
    } catch (error) {
      console.error('Error loading bookings:', error);
    }
  };

  const deleteBooking = async (bookingId) => {
    const booking = bookings.find(b => b.id === bookingId);
    if (!booking) return;

    setConfirmDelete({
      type: 'booking',
      id: bookingId,
      title: 'Видалити запис?',
      message: `${booking.patient_name} - ${booking.booking_time}`
    });
  };

  const handleLogin = async (username, password) => {
    try {
      // ВРЕМЕННО: Разрешить вход с admin/admin (удалить после первого входа)
      if (username === 'admin' && password === 'admin') {
        setIsAuthenticated(true);
        setCurrentUsername(username);
        sessionStorage.setItem('adminAuth', 'true');
        sessionStorage.setItem('adminUsername', username);
        return;
      }

      const { data, error } = await supabase
        .from('admin_credentials')
        .select('*')
        .eq('username', username)
        .single();

      if (error || !data) {
        throw new Error('Невірний логін або пароль');
      }

      if (data.password_hash !== password) {
        throw new Error('Невірний логін або пароль');
      }

      setIsAuthenticated(true);
      setCurrentUsername(username);
      sessionStorage.setItem('adminAuth', 'true');
      sessionStorage.setItem('adminUsername', username);
    } catch (error) {
      throw error;
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setCurrentUsername('admin');
    sessionStorage.removeItem('adminAuth');
    sessionStorage.removeItem('adminUsername');
  };

  const handlePasswordChange = async () => {
    // Валідація
    if (!passwordChange.newUsername && !passwordChange.newPassword) {
      setPasswordMessage({ type: 'error', text: 'Введіть новий логін або пароль' });
      setTimeout(() => setPasswordMessage(null), 3000);
      return;
    }

    if (passwordChange.newPassword && passwordChange.newPassword !== passwordChange.confirmPassword) {
      setPasswordMessage({ type: 'error', text: 'Паролі не співпадають' });
      setTimeout(() => setPasswordMessage(null), 3000);
      return;
    }

    if (passwordChange.newPassword && passwordChange.newPassword.length < 6) {
      setPasswordMessage({ type: 'error', text: 'Пароль має бути мінімум 6 символів' });
      setTimeout(() => setPasswordMessage(null), 3000);
      return;
    }

    if (passwordChange.newUsername && passwordChange.newUsername.length < 3) {
      setPasswordMessage({ type: 'error', text: 'Логін має бути мінімум 3 символи' });
      setTimeout(() => setPasswordMessage(null), 3000);
      return;
    }

    try {
      // Перевірити поточний пароль
      const { data: currentData } = await supabase
        .from('admin_credentials')
        .select('*')
        .eq('username', currentUsername)
        .single();

      if (currentData.password_hash !== passwordChange.currentPassword) {
        setPasswordMessage({ type: 'error', text: 'Невірний поточний пароль' });
        setTimeout(() => setPasswordMessage(null), 3000);
        return;
      }

      // Підготувати дані для оновлення
      const updateData = { updated_at: new Date() };
      if (passwordChange.newUsername) {
        updateData.username = passwordChange.newUsername;
      }
      if (passwordChange.newPassword) {
        updateData.password_hash = passwordChange.newPassword;
      }

      // Оновити дані
      const { error } = await supabase
        .from('admin_credentials')
        .update(updateData)
        .eq('username', currentUsername);

      if (error) throw error;

      // Оновити поточний username якщо він змінився
      if (passwordChange.newUsername) {
        setCurrentUsername(passwordChange.newUsername);
        sessionStorage.setItem('adminUsername', passwordChange.newUsername);
      }

      const changedItems = [];
      if (passwordChange.newUsername) changedItems.push('Логін');
      if (passwordChange.newPassword) changedItems.push('пароль');
      
      setPasswordMessage({ type: 'success', text: `${changedItems.join(' та ')} успішно змінено!` });
      setPasswordChange({ currentPassword: '', newUsername: '', newPassword: '', confirmPassword: '' });
      setTimeout(() => {
        setPasswordMessage(null);
        setShowPasswordChange(false);
      }, 2000);
    } catch (error) {
      console.error('Error changing credentials:', error);
      setPasswordMessage({ type: 'error', text: 'Помилка зміни даних' });
      setTimeout(() => setPasswordMessage(null), 3000);
    }
  };

  useEffect(() => {
    const auth = sessionStorage.getItem('adminAuth');
    const username = sessionStorage.getItem('adminUsername');
    if (auth === 'true') {
      setIsAuthenticated(true);
      if (username) {
        setCurrentUsername(username);
      }
    }
  }, []);

  const closeConfirmModal = () => {
    setIsClosing(true);
    setTimeout(() => {
      setConfirmDelete(null);
      setIsClosing(false);
    }, 300);
  };

  const confirmDeleteAction = async () => {
    if (!confirmDelete) return;

    try {
      const booking = bookings.find(b => b.id === confirmDelete.id);
      if (!booking) return;

      // Видалити запис
      const { error: bookingError } = await supabase
        .from('bookings')
        .delete()
        .eq('id', confirmDelete.id);

      if (bookingError) throw bookingError;

      // Видалити відповідний blocked_slot якщо він існує
      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
      const day = String(selectedDate.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;
      await supabase
        .from('blocked_slots')
        .delete()
        .eq('doctor_name', selectedDoctor)
        .eq('blocked_date', dateStr)
        .eq('blocked_time', booking.booking_time);

      setMessage({ type: 'success', text: 'Запис видалено!' });
      await loadBlockings();
    } catch (error) {
      console.error('Error deleting booking:', error);
      setMessage({ type: 'error', text: 'Помилка видалення' });
    } finally {
      closeConfirmModal();
      setTimeout(() => setMessage(null), 3000);
    }
  };

  const toggleTimeSlot = async (time) => {
    // Перевірити чи це запис клієнта
    const booking = bookings.find(b => b.booking_time === time);
    
    if (booking && selectedTimes.includes(time)) {
      // Якщо це запис клієнта і намагаємось розблокувати - показуємо підтвердження
      setConfirmDelete({
        type: 'booking',
        id: booking.id,
        title: 'Видалити запис клієнта?',
        message: `${booking.patient_name} - ${time}`
      });
      return;
    }
    
    // Звичайне блокування/розблокування
    setSelectedTimes(prev => 
      prev.includes(time) 
        ? prev.filter(t => t !== time)
        : [...prev, time]
    );
  };

  const saveBlockedSlots = async () => {
    setLoading(true);
    setMessage(null);
    
    try {
      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
      const day = String(selectedDate.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;
      
      // Delete all existing slots for this doctor and date
      await supabase
        .from('blocked_slots')
        .delete()
        .eq('doctor_name', selectedDoctor)
        .eq('blocked_date', dateStr);

      // Insert new blocked slots
      if (selectedTimes.length > 0) {
        const slotsToInsert = selectedTimes.map(time => ({
          doctor_name: selectedDoctor,
          blocked_date: dateStr,
          blocked_time: time
        }));

        const { error } = await supabase
          .from('blocked_slots')
          .insert(slotsToInsert);

        if (error) throw error;
      }

      setMessage({ type: 'success', text: 'Розклад збережено!' });
      await loadBlockedSlots();
    } catch (error) {
      console.error('Error saving blocked slots:', error);
      setMessage({ type: 'error', text: 'Помилка збереження' });
    } finally {
      setLoading(false);
      setTimeout(() => setMessage(null), 3000);
    }
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('uk-UA', { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  const changeDate = (days) => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + days);
    setSelectedDate(newDate);
  };

  if (!isAuthenticated) {
    return <AdminLogin onLogin={handleLogin} />;
  }

  return (
    <div className="admin-panel">
      <div className="admin-container">
        <div className="admin-header">
          <div>
            <h1>Адмін панель</h1>
            <p>Управління розкладом лікарів</p>
          </div>
          <button className="logout-btn" onClick={handleLogout}>
            <LogOut size={20} />
            Вийти
          </button>
        </div>

        <div className="admin-content">
          {/* Doctor Selection */}
          <div className="admin-section">
            <h2>Виберіть лікаря</h2>
            <div className="doctor-selector">
              {doctors.map(doctor => (
                <button
                  key={doctor}
                  className={`doctor-btn ${selectedDoctor === doctor ? 'active' : ''}`}
                  onClick={() => setSelectedDoctor(doctor)}
                >
                  {doctor}
                </button>
              ))}
            </div>
          </div>

          {/* Date Selection */}
          <div className="admin-section">
            <h2>Виберіть дату</h2>
            <div className="date-selector">
              <button 
                className="date-nav-btn"
                onClick={() => changeDate(-1)}
              >
                ←
              </button>
              <div className="selected-date">
                <Calendar size={20} />
                <span key={`${selectedDate.getFullYear()}-${selectedDate.getMonth()}-${selectedDate.getDate()}`} className="date-text">
                  {formatDate(selectedDate)}
                </span>
              </div>
              <button 
                className="date-nav-btn"
                onClick={() => changeDate(1)}
              >
                →
              </button>
            </div>
          </div>

          {/* Time Slots */}
          <div className="admin-section">
            <h2>Заблокувати час (лікар зайнятий)</h2>
            <p className="section-hint">Виберіть години, коли лікар недоступний</p>
            {slotsLoaded ? (
              <div className="time-slots-grid" key={animateSlots}>
                {timeSlots.map((time, index) => {
                  const isBlocked = selectedTimes.includes(time);
                  const booking = bookings.find(b => b.booking_time === time);
                  const hasBooking = !!booking;
                  
                  return (
                    <button
                      key={time}
                      className={`time-slot-btn ${isBlocked ? (hasBooking ? 'booked' : 'blocked') : ''}`}
                      onClick={() => toggleTimeSlot(time)}
                      title={hasBooking ? `Запис: ${booking.patient_name} - натисніть щоб видалити` : isBlocked ? 'Заблоковано - натисніть щоб розблокувати' : 'Натисніть щоб заблокувати'}
                      style={{ animationDelay: `${index * 0.03}s` }}
                    >
                      <Clock size={16} />
                      {time}
                      {isBlocked && <X size={16} className="blocked-icon" />}
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="time-slots-grid">
                <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '2rem', color: 'rgba(255, 255, 255, 0.5)' }}>
                  Завантаження...
                </div>
              </div>
            )}
          </div>

          {/* Save Button */}
          <div className="admin-actions">
            <button 
              className="save-btn"
              onClick={saveBlockedSlots}
              disabled={loading}
            >
              <Save size={20} />
              {loading ? 'Збереження...' : 'Зберегти розклад'}
            </button>
          </div>

          {/* Message */}
          {message && (
            <div className={`admin-message ${message.type}`}>
              {message.text}
            </div>
          )}

          {/* Password Change */}
          <div className="admin-section">
            <div className="section-header">
              <h2>Зміна паролю</h2>
              <button 
                className="toggle-section-btn"
                onClick={() => setShowPasswordChange(!showPasswordChange)}
              >
                <Lock size={18} />
                <span key={showPasswordChange ? 'hide' : 'show'}>
                  {showPasswordChange ? 'Сховати' : 'Змінити'}
                </span>
              </button>
            </div>

            <div className={`telegram-settings ${showPasswordChange ? 'open' : ''}`}>
              <div className="telegram-settings-content">
                <p className="section-hint">
                  Змініть логін та/або пароль для входу в адмін панель
                </p>

                <div className="form-group">
                  <label>
                    <Lock size={18} />
                    Поточний пароль
                  </label>
                  <input
                    type="password"
                    value={passwordChange.currentPassword}
                    onChange={(e) => setPasswordChange({...passwordChange, currentPassword: e.target.value})}
                    placeholder="Введіть поточний пароль для підтвердження"
                    className="telegram-input"
                  />
                  <small className="input-hint">
                    Поточний логін: <strong>{currentUsername}</strong>
                  </small>
                </div>

                <div className="form-group">
                  <label>
                    <User size={18} />
                    Новий логін (необов'язково)
                  </label>
                  <input
                    type="text"
                    value={passwordChange.newUsername}
                    onChange={(e) => setPasswordChange({...passwordChange, newUsername: e.target.value})}
                    placeholder="Введіть новий логін"
                    className="telegram-input"
                  />
                  <small className="input-hint">
                    Мінімум 3 символи. Залиште порожнім, щоб не змінювати
                  </small>
                </div>

                <div className="form-group">
                  <label>
                    <Lock size={18} />
                    Новий пароль (необов'язково)
                  </label>
                  <input
                    type="password"
                    value={passwordChange.newPassword}
                    onChange={(e) => setPasswordChange({...passwordChange, newPassword: e.target.value})}
                    placeholder="Введіть новий пароль"
                    className="telegram-input"
                  />
                  <small className="input-hint">
                    Мінімум 6 символів. Залиште порожнім, щоб не змінювати
                  </small>
                </div>

                {passwordChange.newPassword && (
                  <div className="form-group">
                    <label>
                      <Lock size={18} />
                      Підтвердіть новий пароль
                    </label>
                    <input
                      type="password"
                      value={passwordChange.confirmPassword}
                      onChange={(e) => setPasswordChange({...passwordChange, confirmPassword: e.target.value})}
                      placeholder="Повторіть новий пароль"
                      className="telegram-input"
                    />
                  </div>
                )}

                <div className="telegram-actions">
                  <button
                    className="save-btn"
                    onClick={handlePasswordChange}
                    disabled={!passwordChange.currentPassword || (!passwordChange.newUsername && !passwordChange.newPassword)}
                  >
                    <Save size={18} />
                    Зберегти зміни
                  </button>
                </div>

                {passwordMessage && (
                  <div className={`admin-message ${passwordMessage.type}`}>
                    {passwordMessage.text}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Telegram Settings */}
          <div className="admin-section">
            <div className="section-header">
              <h2>Налаштування Telegram</h2>
              <button 
                className="toggle-section-btn"
                onClick={() => setShowTelegramSettings(!showTelegramSettings)}
              >
                <Settings size={18} />
                <span key={showTelegramSettings ? 'hide' : 'show'}>
                  {showTelegramSettings ? 'Сховати' : 'Налаштувати'}
                </span>
              </button>
            </div>

            <div className={`telegram-settings ${showTelegramSettings ? 'open' : ''}`}>
              <div className="telegram-settings-content">
                <p className="section-hint">
                  Налаштуйте Telegram бота для отримання нотифікацій про нові записи
                </p>

                <div className="form-group">
                  <label>
                    <Send size={18} />
                    Bot Token
                  </label>
                  <input
                    type="text"
                    value={telegramSettings.bot_token}
                    onChange={(e) => handleTelegramSettingsChange('bot_token', e.target.value)}
                    placeholder="1234567890:ABCdefGHIjklMNOpqrsTUVwxyz"
                    className="telegram-input"
                  />
                  <small className="input-hint">
                    Отримайте токен у @BotFather в Telegram
                  </small>
                </div>

                <div className="form-group">
                  <label>
                    <Users size={18} />
                    Username отримувача
                  </label>
                  <input
                    type="text"
                    value={telegramSettings.chat_username}
                    onChange={(e) => handleTelegramSettingsChange('chat_username', e.target.value)}
                    placeholder="@username"
                    className="telegram-input"
                  />
                  <small className="input-hint">
                    Ваш Telegram username (спочатку напишіть боту /start)
                  </small>
                </div>

                <div className="telegram-actions">
                  <button
                    className="test-btn"
                    onClick={handleTestTelegram}
                    disabled={telegramLoading || !telegramSettings.bot_token || !telegramSettings.chat_username}
                  >
                    <Send size={18} />
                    {telegramLoading ? 'Тестування...' : 'Тест підключення'}
                  </button>

                  <button
                    className="save-btn"
                    onClick={handleSaveTelegramSettings}
                    disabled={telegramLoading || !telegramSettings.bot_token || !telegramSettings.chat_username}
                  >
                    <Save size={18} />
                    {telegramLoading ? 'Збереження...' : 'Зберегти'}
                  </button>
                </div>

                {telegramMessage && (
                  <div className={`admin-message ${telegramMessage.type}`}>
                    {telegramMessage.text}
                  </div>
                )}

                <div className="telegram-info">
                  <h4>📱 Як налаштувати:</h4>
                  <ol>
                    <li>Створіть бота через @BotFather в Telegram</li>
                    <li>Скопіюйте Bot Token і вставте вище</li>
                    <li>Напишіть вашому боту /start</li>
                    <li>Вкажіть ваш @username</li>
                    <li>Натисніть "Тест підключення"</li>
                    <li>Якщо тест успішний - натисніть "Зберегти"</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          {/* Bookings List */}
          <div className="admin-section">
            <div className="section-header">
              <h2>Записи на цю дату</h2>
              <button 
                className="toggle-section-btn"
                onClick={() => {
                  setShowBookings(!showBookings);
                  if (!showBookings) loadBookings();
                }}
              >
                <Users size={18} />
                <span key={showBookings ? 'hide' : 'show'}>
                  {showBookings ? 'Сховати' : 'Показати'}
                </span>
              </button>
            </div>
            
            <div className={`bookings-list ${showBookings ? 'open' : ''}`}>
              <div>
                {bookings.length === 0 ? (
                  <p style={{ textAlign: 'center', color: '#666', padding: '2rem' }}>
                    Немає записів на цю дату
                  </p>
                ) : (
                  <div className="bookings-grid" key={`bookings-${selectedDoctor}-${selectedDate.getFullYear()}-${selectedDate.getMonth()}-${selectedDate.getDate()}`}>
                    {bookings.map(booking => (
                      <div key={booking.id} className="booking-card">
                        <div className="booking-time">
                          <Clock size={16} />
                          {booking.booking_time}
                        </div>
                        <div className="booking-info">
                          <strong>{booking.patient_name}</strong>
                          <p>{booking.patient_phone}</p>
                          {booking.patient_email && <p>{booking.patient_email}</p>}
                          <p className="booking-service">{booking.service_title}</p>
                        </div>
                        <div className={`booking-status ${booking.status}`}>
                          {booking.status === 'confirmed' && '✓ Підтверджено'}
                          {booking.status === 'cancelled' && '✗ Скасовано'}
                        </div>
                        <button
                          className="delete-booking-btn"
                          onClick={() => deleteBooking(booking.id)}
                          title="Видалити запис"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Confirm Delete Modal */}
      {confirmDelete && (
        <div className={`confirm-modal-overlay ${isClosing ? 'closing' : ''}`} onClick={closeConfirmModal}>
          <div className={`confirm-modal ${isClosing ? 'closing' : ''}`} onClick={(e) => e.stopPropagation()}>
            <div className="confirm-modal-header">
              <h3>{confirmDelete.title}</h3>
              <button className="confirm-modal-close" onClick={closeConfirmModal}>
                <X size={20} />
              </button>
            </div>
            <div className="confirm-modal-body">
              <p>{confirmDelete.message}</p>
            </div>
            <div className="confirm-modal-actions">
              <button className="confirm-modal-cancel" onClick={closeConfirmModal}>
                Скасувати
              </button>
              <button className="confirm-modal-delete" onClick={confirmDeleteAction}>
                <Trash2 size={18} />
                Видалити
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
